import type { ReactNode } from "react";
import {
  useFormContext,
  type Control,
  type FieldPath,
  type FieldValues
} from "react-hook-form";

/** Props every bound field accepts. */
export type BaseFieldProps<T extends FieldValues> = {
  /** Defaults to the surrounding `<Form>`'s control. */
  control?: Control<T>;
  name: FieldPath<T>;
  label: ReactNode;
  required?: boolean;
  description?: ReactNode;
  disabled?: boolean;
  className?: string;
  id?: string;
};

export type SelectOption = { value: string; label: string };
export type SelectOptionGroup = { label: string; options: SelectOption[] };

export const toOptions = (
  options: ReadonlyArray<string | SelectOption>
): SelectOption[] =>
  options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));

/** The `control` prop when given, else the nearest `<Form>`. */
export function useFieldControl<T extends FieldValues>(
  control?: Control<T>
): Control<T> {
  const ctx = useFormContext<T>();
  const resolved = control ?? ctx?.control;
  if (!resolved) {
    throw new Error(
      "Form fields need a `control` prop or a surrounding <Form>."
    );
  }
  return resolved;
}
